import { useEffect, useState } from "react";
import api from "../lib/api";
import { Cloud, CloudRain, CloudSnow, CloudFog, Sun, CloudSun, Zap, Wind, ExternalLink } from "lucide-react";

export const WX_DESC = {
  0: "Clear sky", 1: "Mainly clear", 2: "Partly cloudy", 3: "Overcast",
  45: "Fog", 48: "Rime fog", 51: "Light drizzle", 53: "Drizzle", 55: "Heavy drizzle",
  61: "Light rain", 63: "Rain", 65: "Heavy rain", 66: "Freezing rain", 67: "Freezing rain",
  71: "Light snow", 73: "Snow", 75: "Heavy snow", 77: "Snow grains",
  80: "Rain showers", 81: "Rain showers", 82: "Violent showers",
  85: "Snow showers", 86: "Snow showers", 95: "Thunderstorm", 96: "Thunderstorm", 99: "Thunderstorm",
};

export const WxIcon = ({ code, className }) => {
  if (code === 0 || code === 1) return <Sun className={className} />;
  if (code === 2) return <CloudSun className={className} />;
  if (code === 3) return <Cloud className={className} />;
  if ([45, 48].includes(code)) return <CloudFog className={className} />;
  if ([71, 73, 75, 77, 85, 86].includes(code)) return <CloudSnow className={className} />;
  if ([95, 96, 99].includes(code)) return <Zap className={className} />;
  if (code >= 51) return <CloudRain className={className} />;
  return <CloudSun className={className} />;
};

export default function Weather({ lat, lon, label, compact = false }) {
  const [w, setW] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    if (lat == null || lon == null) { setErr(true); return; }
    let alive = true;
    setW(null); setErr(false);
    const key = `wx_${lat.toFixed(2)}_${lon.toFixed(2)}`;
    try {
      const c = JSON.parse(localStorage.getItem(key) || "null");
      if (c && Date.now() - c.t < 600000) { setW(c.d); return; }
    } catch {}
    api.get("/weather", { params: { lat, lon } })
      .then((r) => {
        if (!alive) return;
        setW(r.data);
        try { localStorage.setItem(key, JSON.stringify({ t: Date.now(), d: r.data })); } catch {}
      })
      .catch(() => { if (alive) setErr(true); });
    return () => { alive = false; };
  }, [lat, lon]);

  if (err) return null;
  if (!w) {
    return <div className={`animate-pulse bg-white/20 rounded ${compact ? "h-5 w-24" : "h-16 w-40"}`} />;
  }

  const c = Math.round(w.temperature_c);
  const f = Math.round(c * 9 / 5 + 32);
  const code = w.weather_code;
  const desc = WX_DESC[code] || "—";
  const windMph = Math.round((w.wind_kmh || 0) * 0.621371);

  if (compact) {
    return (
      <span className="inline-flex items-center gap-1.5 text-sm" data-testid="weather-compact">
        <WxIcon code={code} className="w-4 h-4" /> {f}°F / {c}°C{label ? ` — ${label}` : ""}
      </span>
    );
  }

  return (
    <div className="flex items-center gap-4" data-testid="weather-card">
      <WxIcon code={code} className="w-12 h-12 text-white/90 shrink-0" />
      <div>
        <div className="font-heading font-900 text-3xl text-white leading-none">{f}°F <span className="text-white/70 text-xl">/ {c}°C</span></div>
        <div className="text-white/80 text-sm mt-1 flex items-center gap-3">
          <span>{desc}</span>
          <span className="inline-flex items-center gap-1"><Wind className="w-3.5 h-3.5" /> {windMph} mph</span>
        </div>
        <a href="https://naturepulse.cloud/" target="_blank" rel="noreferrer" data-testid="naturepulse-link"
          className="text-white/70 hover:text-white text-xs inline-flex items-center gap-1 mt-1">
          Weather & almanac on NaturePulse <ExternalLink className="w-3 h-3" />
        </a>
      </div>
    </div>
  );
}
